import React, { useEffect, useRef } from 'react';
import { Controller, FieldError, useFormContext } from 'react-hook-form';
import { CharitableGoalDataDTO } from '../../CharitableGiving.meta';
import {
  AccountDTO,
  useAccountsQuery,
} from '../../../../../../CharitableGivingWorkflow/stages/CharitableGiving/api/useAccountsQuery';
import { Button } from '../../../../../../CommonComponents/OPL/Button/Button';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { formatCurrency } from '../../../../../../CommonUtils/formatNumber';
import {
  AddAccountSelectionModalWrapper,
  Selection,
} from '../../../../../../Profile/Accounts/components/AddAccountSelectionModalWrapper/AddAccountSelectionModalWrapper';
import { AccountModalUsageContext } from '../../../../../../Profile/Accounts/Features/AccountPreferences/components/AccountForm/context/AccountFormProvider.meta';
import { Select } from '../../../../../../CommonComponents/OPL/Select/Select/Select';
import { Shimmer } from '../../../../../../CommonComponents/OPL/Shimmer/Shimmer';
import { Text } from '../../../../../../CommonComponents/OPL/Text/Text';
import { GivingStrategy } from '../../../../../../CharitableGivingWorkflow/stages/CharitableGiving/api/useCharitableGivingQuery';

interface Props {
  label?: string;
}

export const FundAccount = ({ label = 'Which account(s) will fund the DAF?' }: Props) => {
  const { watch } = useFormContext<CharitableGoalDataDTO>();

  const [givingStrategy, donor, kindOfProperty] = watch([
    'givingStrategy',
    'donor',
    'kindOfProperty',
  ]);

  const { isLoading, data } = useAccountsQuery({
    givingStrategy,
    donor,
    kindOfProperty,
  });

  if (isLoading) {
    return (
      <Box
        display="grid"
        alignItems="end"
        css={`
          grid-template-columns: 1fr 150px;
          gap: 24px 16px;
        `}
      >
        <Box>
          <Text variant="formLabel">{label}</Text>

          <Shimmer width="100%" height="37px" mt={4} />
        </Box>

        <Button variant="subtleActive" leftIcon="fa fa-plus" onClick={() => {}} disabled>
          Add Account
        </Button>
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  return <Content accounts={data} />;
};

interface ContentProps {
  accounts: AccountDTO[];
}

const Content = ({ accounts }: ContentProps) => {
  const { control, formState, setValue, watch, getValues } = useFormContext<
    CharitableGoalDataDTO
  >();

  const [givingStrategy, donor, kindOfProperty = null] = watch([
    'givingStrategy',
    'donor',
    'kindOfProperty',
  ]);

  const [isOpenAddAccountModal, setIsOpenAddAccountModal] = React.useState(false);

  /**
   * Effect to allow auto-selecting newly added accounts
   */
  const fundAccountsRef = useRef(accounts.map(({ id }) => id));
  useEffect(() => {
    if (accounts.length !== fundAccountsRef.current.length) {
      const newFundAccounts = accounts.reduce((acc, { id }) => {
        if (!fundAccountsRef.current.includes(id)) {
          acc.push(id);
        }

        return acc;
      }, [] as string[]);

      const currentFundAccounts = getValues('fundAccounts');

      setValue('fundAccounts', [...(currentFundAccounts ?? []), ...newFundAccounts]);

      fundAccountsRef.current = accounts.map(({ id }) => id);
    }
  }, [accounts, getValues, setValue]);

  const accountOptions = accounts.map(({ id, name, institution, balance }) => ({
    value: id,
    label: name,
    subLabel: `${institution ?? ''} ${balance ? `- ${formatCurrency(balance)}` : ''}`,
  }));

  return (
    <Box
      display="grid"
      alignItems="end"
      css={`
        grid-template-columns: 1fr 150px;
        gap: 24px 16px;
      `}
    >
      <Controller
        control={control}
        name="fundAccounts"
        render={({ field: { value, onChange, disabled, ...field } }) => {
          const isSelectDisabled =
            (givingStrategy === GivingStrategy.DonationToDAF && kindOfProperty === null) ||
            disabled;

          return (
            <Select
              {...field}
              disabled={isSelectDisabled}
              isPortal
              isMulti
              label={
                givingStrategy === GivingStrategy.DonationToDAF
                  ? 'Which account(s) will fund the DAF?'
                  : 'Which account(s) will fund this goal?'
              }
              value={accountOptions.filter(opt => (value || []).some(v => v === opt.value))}
              options={accountOptions}
              onChange={opts => onChange(opts.map(opt => opt.value))}
              formVariant={formState.errors.fundAccounts ? 'error' : undefined}
              helperText={(formState.errors.fundAccounts as FieldError)?.message}
              tooltip={
                givingStrategy === GivingStrategy.DonationToDAF && kindOfProperty === null
                  ? 'Please fill Property field to see the options'
                  : undefined
              }
            />
          );
        }}
      />

      {!formState.disabled && (
        <Button
          variant="subtleActive"
          leftIcon="fa fa-plus"
          disabled={givingStrategy === GivingStrategy.DonationToDAF && kindOfProperty === null}
          onClick={() => {
            setIsOpenAddAccountModal(true);
          }}
        >
          Add Account
        </Button>
      )}

      {isOpenAddAccountModal && (
        <AddAccountSelectionModalWrapper
          initialSelection={Selection.Manual}
          usageContext={AccountModalUsageContext.CharitableGivingWorkflow}
          usageContextOptions={{
            CharitableGivingDonor: donor,
            CharitableGivingStrategy: givingStrategy,
            CharitableGivingKindOfProperty: kindOfProperty,
          }}
          showExternalProviders={false}
          onClose={() => setIsOpenAddAccountModal(false)}
        />
      )}
    </Box>
  );
};
